import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Inbox, Users, LogOut, Plus, Pencil, Trash2, Star, Eye, EyeOff, Archive, X, Mail, ExternalLink, FileText, Link2, UserSquare, Handshake, Upload, CreditCard, Heart,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { CATEGORIES } from "../lib/constants";
import { fullDate, timeRange } from "../lib/date";
import { PagesTab } from "../components/admin/PagesTab";
import { LinksTab } from "../components/admin/LinksTab";
import { BoardTab } from "../components/admin/BoardTab";
import { PartnersTab } from "../components/admin/PartnersTab";
import { MembershipTab } from "../components/admin/MembershipTab";

const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const localInputToIso = (v) => (v ? new Date(v).toISOString() : null);

const emptyEvent = {
  title: "", category: "Soul", start: "", end: "", location: "", description: "",
  image_url: "", tigerlink_url: "", featured: false, status: "published",
};

/* ---------------- Login screen ---------------- */
function AdminLogin({ authError }) {
  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-paper px-6" data-testid="admin-login">
      <div className="mb-8"><Logo className="h-11" /></div>
      <p className="font-sans text-xs tracking-[0.24em] uppercase text-trailride font-semibold mb-4">Admin</p>
      <h1 className="font-serif text-4xl md:text-5xl font-black text-ink mb-4 text-center">Dashboard access.</h1>
      <p className="font-sans text-ink/60 text-center max-w-sm mb-8">
        Sign in with your authorized Google account to manage events, inquiries, and members.
      </p>
      {authError && (
        <p className="font-sans text-sm text-red-600 mb-4" data-testid="admin-auth-error">
          That account isn't authorized for admin access.
        </p>
      )}
      <button
        onClick={startLogin}
        data-testid="admin-google-login"
        className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ink text-paper font-sans font-medium hover:-translate-y-0.5 transition-all duration-300"
      >
        Continue with Google
      </button>
      <Link to="/" className="font-sans text-sm text-ink/40 hover:text-ink mt-8">← Back to site</Link>
    </div>
  );
}

/* ---------------- Event editor modal ---------------- */
function EventEditor({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? { ...initial, start: isoToLocalInput(initial.start), end: isoToLocalInput(initial.end) }
      : emptyEvent
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title, category: form.category,
      start: localInputToIso(form.start), end: localInputToIso(form.end),
      location: form.location, description: form.description,
      image_url: form.image_url || null, tigerlink_url: form.tigerlink_url || null,
      featured: !!form.featured, status: form.status,
    };
    try {
      if (editing) await api.put(`/admin/events/${initial.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(editing ? "Event updated" : "Event created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Could not save event");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-ink/15 rounded-md py-2.5 px-3 text-ink text-sm focus:outline-none focus:border-trailride transition-colors";

  return (
    <div className="fixed inset-0 z-[60] bg-ink/40 flex items-start md:items-center justify-center overflow-y-auto p-4" data-testid="event-editor">
      <div className="bg-paper w-full max-w-2xl rounded-lg my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="font-serif text-xl font-bold text-ink">{editing ? "Edit event" : "New event"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="event-editor-close" className="text-ink/50 hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="font-sans text-xs text-ink/60">Title</label>
            <input required value={form.title} onChange={set("title")} data-testid="ee-title" className={inputCls} />
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">Category</label>
            <select value={form.category} onChange={set("category")} data-testid="ee-category" className={inputCls}>
              {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">Status</label>
            <select value={form.status} onChange={set("status")} data-testid="ee-status" className={inputCls}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">Start</label>
            <input type="datetime-local" required value={form.start} onChange={set("start")} data-testid="ee-start" className={inputCls} />
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">End</label>
            <input type="datetime-local" value={form.end} onChange={set("end")} data-testid="ee-end" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="font-sans text-xs text-ink/60">Location</label>
            <input required value={form.location} onChange={set("location")} data-testid="ee-location" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="font-sans text-xs text-ink/60">Description</label>
            <textarea required rows={3} value={form.description} onChange={set("description")} data-testid="ee-description" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="font-sans text-xs text-ink/60">Image URL</label>
            <input value={form.image_url || ""} onChange={set("image_url")} data-testid="ee-image" placeholder="https://…" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="font-sans text-xs text-ink/60">TigerLink RSVP URL</label>
            <input value={form.tigerlink_url || ""} onChange={set("tigerlink_url")} data-testid="ee-tigerlink" placeholder="https://lsu.campuslabs.com/…" className={inputCls} />
          </div>
          <label className="md:col-span-2 flex items-center gap-2 font-sans text-sm text-ink cursor-pointer">
            <input type="checkbox" checked={!!form.featured} onChange={set("featured")} data-testid="ee-featured" className="accent-[#4B1B68] w-4 h-4" />
            Feature on homepage
          </label>
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-ink/20 font-sans text-sm text-ink hover:bg-ink/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="ee-save" className="px-6 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60">
              {saving ? "Saving…" : "Save event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Events tab ---------------- */
function EventsTab() {
  const [events, setEvents] = useState([]);
  const [editor, setEditor] = useState(null); // null | {} | event
  const load = useCallback(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const quickUpdate = async (ev, changes) => {
    const payload = {
      title: ev.title, category: ev.category, start: ev.start, end: ev.end,
      location: ev.location, description: ev.description, image_url: ev.image_url,
      tigerlink_url: ev.tigerlink_url, featured: ev.featured, status: ev.status, ...changes,
    };
    try {
      await api.put(`/admin/events/${ev.id}`, payload);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try { await api.delete(`/admin/events/${ev.id}`); toast.success("Event deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const statusBadge = (s) =>
    s === "published" ? "bg-trailride text-white" : s === "draft" ? "bg-ink/10 text-ink/70" : "bg-ink/70 text-white";

  return (
    <div data-testid="admin-events-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl font-bold text-ink">Events</h2>
        <button onClick={() => setEditor({})} data-testid="admin-new-event" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark">
          <Plus size={16} /> New event
        </button>
      </div>

      <div className="border border-ink/10 rounded-lg divide-y divide-ink/10">
        {events.length === 0 && <p className="p-6 font-sans text-ink/50">No events yet.</p>}
        {events.map((ev) => (
          <div key={ev.id} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5" data-testid={`admin-event-row-${ev.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {ev.featured && <Star size={14} className="text-trailride fill-trailride" />}
                <span className="font-sans font-semibold text-ink truncate">{ev.title}</span>
                <span className={`font-sans text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full ${statusBadge(ev.status)}`}>{ev.status}</span>
                <span className="font-sans text-[10px] tracking-wide uppercase text-ink/50 border border-ink/15 rounded-full px-2 py-0.5">{ev.category}</span>
              </div>
              <p className="font-sans text-sm text-ink/55 mt-1">{fullDate(ev.start)} · {timeRange(ev.start, ev.end)}</p>
            </div>
            <div className="flex items-center gap-1">
              <button title={ev.featured ? "Unfeature" : "Feature"} onClick={() => quickUpdate(ev, { featured: !ev.featured })} data-testid={`admin-feature-${ev.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Star size={16} className={ev.featured ? "fill-trailride text-trailride" : ""} /></button>
              {ev.status === "published" ? (
                <button title="Unpublish (draft)" onClick={() => quickUpdate(ev, { status: "draft" })} data-testid={`admin-unpublish-${ev.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><EyeOff size={16} /></button>
              ) : (
                <button title="Publish" onClick={() => quickUpdate(ev, { status: "published" })} data-testid={`admin-publish-${ev.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Eye size={16} /></button>
              )}
              <button title="Archive" onClick={() => quickUpdate(ev, { status: "archived" })} data-testid={`admin-archive-${ev.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Archive size={16} /></button>
              <button title="Edit" onClick={() => setEditor(ev)} data-testid={`admin-edit-${ev.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Pencil size={16} /></button>
              <button title="Delete" onClick={() => remove(ev)} data-testid={`admin-delete-${ev.id}`} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editor && <EventEditor initial={editor.id ? editor : null} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}

/* ---------------- Inquiries tab ---------------- */
function InquiriesTab() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => { try { await api.put(`/admin/inquiries/${id}/read`); load(); } catch {} };

  return (
    <div data-testid="admin-inquiries-tab">
      <h2 className="font-serif text-2xl font-bold text-ink mb-6">Partnership inquiries</h2>
      {items.length === 0 ? (
        <p className="font-sans text-ink/50 border border-ink/10 rounded-lg p-6">No inquiries yet.</p>
      ) : (
        <div className="space-y-4">
          {items.map((q) => (
            <div key={q.id} className={`border rounded-lg p-5 ${q.read ? "border-ink/10" : "border-trailride/40 bg-trailride/[0.03]"}`} data-testid={`inquiry-${q.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-sans font-semibold text-ink">{q.name} {q.organization && <span className="text-ink/50 font-normal">· {q.organization}</span>}</p>
                  <a href={`mailto:${q.email}`} className="font-sans text-sm text-trailride inline-flex items-center gap-1"><Mail size={13} /> {q.email}</a>
                </div>
                <span className="font-sans text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-ink/5 text-ink/60">{q.interest}</span>
              </div>
              <p className="font-sans text-sm text-ink/75 mt-3 leading-relaxed whitespace-pre-line">{q.message}</p>
              {!q.read && (
                <button onClick={() => markRead(q.id)} data-testid={`inquiry-read-${q.id}`} className="font-sans text-xs text-trailride font-medium mt-3 hover:underline">Mark as read</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Members tab ---------------- */
function MembersTab() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "member", dues_status: "unpaid" });
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/members", form);
      toast.success("Member added");
      setForm({ name: "", email: "", role: "member", dues_status: "unpaid" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add member"); }
  };

  const parseBulk = (text) => {
    const rows = [];
    text.split(/\r?\n/).forEach((line) => {
      const t = line.trim();
      if (!t) return;
      const parts = t.split(",").map((p) => p.trim());
      const lower = t.toLowerCase();
      // skip a header row
      if (lower.includes("email") && (lower.includes("name") || lower.startsWith("email"))) return;
      let name = "", email = "", role = "member", dues = "unpaid";
      if (parts.length === 1) {
        email = parts[0];
      } else {
        [name, email, role, dues] = [parts[0], parts[1], parts[2], parts[3]];
      }
      role = (role || "").toLowerCase() === "officer" ? "officer" : "member";
      dues = (dues || "").toLowerCase() === "paid" ? "paid" : "unpaid";
      rows.push({ name: name || "", email: email || "", role, dues_status: dues });
    });
    return rows;
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBulkText((prev) => (prev ? prev + "\n" : "") + ev.target.result);
    reader.readAsText(file);
  };

  const submitBulk = async () => {
    const rows = parseBulk(bulkText);
    if (rows.length === 0) { toast.error("Nothing to import. Add one member per line."); return; }
    setBulkBusy(true);
    try {
      const res = await api.post("/admin/members/bulk", { members: rows });
      const { created, skipped } = res.data;
      toast.success(`Imported ${created} member${created === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""}`);
      setBulkText("");
      setShowBulk(false);
      load();
    } catch { toast.error("Bulk import failed"); }
    finally { setBulkBusy(false); }
  };

  const update = async (m, changes) => {
    const payload = { name: m.name, email: m.email, role: m.role, dues_status: m.dues_status, ...changes };
    try { await api.put(`/admin/members/${m.id}`, payload); load(); } catch { toast.error("Update failed"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await api.delete(`/admin/members/${m.id}`); load(); } catch { toast.error("Delete failed"); }
  };

  const duesAction = async (m, action) => {
    if (!action) return;
    if (action === "refund" && !window.confirm(`Refund ${m.name}'s dues? This attempts a Stripe refund and marks them unpaid.`)) return;
    try {
      const res = await api.put(`/admin/members/${m.id}/dues`, { action });
      toast.success(action === "refund" ? (res.data?.refunded_in_stripe ? "Refunded in Stripe" : "Marked unpaid (no Stripe charge found)") : "Updated");
      load();
    } catch { toast.error("Action failed"); }
  };

  const STATUS_STYLE = {
    active: "bg-trailride text-white", active_donor: "bg-trailride text-white",
    expired: "bg-amber-100 text-amber-800 border border-amber-300", unpaid: "bg-ink/5 text-ink/60 border border-ink/15",
  };

  const inputCls = "bg-transparent border border-ink/15 rounded-md py-2 px-3 text-ink text-sm focus:outline-none focus:border-trailride";

  return (
    <div data-testid="admin-members-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl font-bold text-ink">Members &amp; dues</h2>
        <button
          onClick={() => setShowBulk((s) => !s)}
          data-testid="member-bulk-toggle"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-ink/20 font-sans text-sm font-medium text-ink hover:border-trailride hover:text-trailride transition-colors"
        >
          <Upload size={16} /> Bulk upload
        </button>
      </div>

      {showBulk && (
        <div className="border border-ink/10 rounded-lg p-5 mb-8 bg-ink/[0.015]" data-testid="member-bulk-panel">
          <p className="font-sans text-sm text-ink/70 mb-1">Paste one member per line, or upload a .csv file.</p>
          <p className="font-sans text-xs text-ink/45 mb-3">Format: <code className="text-trailride">name, email, role, dues</code> — role (member/officer) and dues (paid/unpaid) are optional. Email only also works.</p>
          <textarea
            rows={6}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            data-testid="member-bulk-text"
            placeholder={"Jordan Carter, jordan@lsu.edu, member, paid\nAlexis Rivera, alexis@lsu.edu, officer, unpaid\nsam@lsu.edu"}
            className="w-full bg-paper border border-ink/15 rounded-md py-2 px-3 text-ink text-sm font-mono focus:outline-none focus:border-trailride"
          />
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <label className="inline-flex items-center gap-2 font-sans text-sm text-ink/70 border border-ink/20 rounded-full px-4 py-2 cursor-pointer hover:border-trailride hover:text-trailride transition-colors">
              <Upload size={14} /> Choose .csv
              <input type="file" accept=".csv,text/csv,text/plain" onChange={handleFile} data-testid="member-bulk-file" className="hidden" />
            </label>
            <button
              onClick={submitBulk}
              disabled={bulkBusy}
              data-testid="member-bulk-submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60"
            >
              {bulkBusy ? "Importing…" : "Import members"}
            </button>
            <span className="font-sans text-xs text-ink/45">{parseBulk(bulkText).length} row(s) detected</span>
          </div>
        </div>
      )}

      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8 border border-ink/10 rounded-lg p-4" data-testid="member-add-form">
        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-add-name" className={inputCls} />
        <input required type="email" placeholder="lsu email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-add-email" className={inputCls} />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="member-add-role" className={inputCls}>
          <option value="member">Member</option>
          <option value="officer">Officer</option>
        </select>
        <select value={form.dues_status} onChange={(e) => setForm({ ...form, dues_status: e.target.value })} data-testid="member-add-dues" className={inputCls}>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
        </select>
        <button type="submit" data-testid="member-add-submit" className="inline-flex items-center justify-center gap-2 rounded-md bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark py-2"><Plus size={16} /> Add</button>
      </form>

      <div className="border border-ink/10 rounded-lg divide-y divide-ink/10">
        {members.length === 0 && <p className="p-6 font-sans text-ink/50">No members yet.</p>}
        {members.map((m) => {
          const st = m.account_status || { code: "unpaid", label: "Account created · dues unpaid" };
          return (
          <div key={m.id} className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4" data-testid={`member-row-${m.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-ink">{m.name}</p>
              <p className="font-sans text-sm text-ink/55">{m.email}</p>
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                <span className={`font-sans text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLE[st.code] || STATUS_STYLE.unpaid}`} data-testid={`member-status-${m.id}`}>{st.label}</span>
                {(m.donation_amount || 0) > 0 && <span className="font-sans text-[11px] text-trailride inline-flex items-center gap-1"><Heart size={11} /> ${Number(m.donation_amount).toFixed(0)}</span>}
                {m.dues_expires_at && <span className="font-sans text-[11px] text-ink/45">exp {new Date(m.dues_expires_at).toLocaleDateString()}</span>}
              </div>
            </div>
            <select value={m.role} onChange={(e) => update(m, { role: e.target.value })} data-testid={`member-role-${m.id}`} className={inputCls}>
              <option value="member">Member</option>
              <option value="officer">Officer</option>
            </select>
            <select value="" onChange={(e) => { duesAction(m, e.target.value); e.target.value = ""; }} data-testid={`member-dues-action-${m.id}`} className={inputCls}>
              <option value="">Dues action…</option>
              <option value="mark_paid">Mark paid</option>
              <option value="waive">Waive dues</option>
              <option value="unpaid">Set unpaid</option>
              <option value="refund">Refund</option>
            </select>
            <button onClick={() => remove(m)} data-testid={`member-delete-${m.id}`} className="p-2 rounded-md hover:bg-red-50 text-red-500 self-start lg:self-auto"><Trash2 size={16} /></button>
          </div>
        );})}
      </div>
    </div>
  );
}

/* ---------------- Admin shell ---------------- */
export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("events");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <p className="font-sans text-ink/40 animate-pulse">Loading…</p>
      </div>
    );
  }

  if (!user) return <AdminLogin authError={window.history.state?.usr?.authError} />;
  if (!user.is_admin) return <AdminLogin authError />;

  const tabs = [
    { id: "events", label: "Events", icon: Calendar },
    { id: "membership", label: "Membership", icon: CreditCard },
    { id: "board", label: "Board", icon: UserSquare },
    { id: "partners", label: "Partners", icon: Handshake },
    { id: "pages", label: "Pages", icon: FileText },
    { id: "links", label: "Links", icon: Link2 },
    { id: "inquiries", label: "Inquiries", icon: Inbox },
    { id: "members", label: "Members", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-ink/[0.015]" data-testid="admin-dashboard">
      <header className="bg-paper border-b border-ink/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex"><Logo className="h-6" /></Link>
          <div className="flex items-center gap-4">
            <a href="/links" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1 font-sans text-sm text-ink/60 hover:text-ink">View site <ExternalLink size={13} /></a>
            <span className="hidden sm:inline font-sans text-sm text-ink/50">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 font-sans text-sm text-ink/70 hover:text-ink border border-ink/15 rounded-full px-4 py-1.5"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-2 mb-10 border-b border-ink/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 font-sans text-sm px-4 py-3 -mb-px border-b-2 transition-colors ${
                tab === t.id ? "border-trailride text-trailride font-semibold" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "events" && <EventsTab />}
        {tab === "membership" && <MembershipTab />}
        {tab === "board" && <BoardTab />}
        {tab === "partners" && <PartnersTab />}
        {tab === "pages" && <PagesTab />}
        {tab === "links" && <LinksTab />}
        {tab === "inquiries" && <InquiriesTab />}
        {tab === "members" && <MembersTab />}
      </div>
    </div>
  );
}
